import { useEffect, useMemo, useState } from 'react';
import {
  AMT_UI_SCALE,
  CCY_POSITION,
  CCY_SORT_ORDER,
  CCY_SYMBOLS,
  D,
  MONEY_SCALE,
  RATE_UI_SCALE,
  type CcyCode,
} from '@wave/shared';
import { useCurrencies, useUsdtRates } from '../api/hooks';
import { IconRefresh, IconSettings } from '../components/icons';
import { useCreateCurrency, useRefreshRate, useToggleAutoManaged, useUpdateUsdtRate } from '../api/mutations';
import { RateSourcesModal } from '../components/modals/RateSourcesModal';
import {
  btnGhostSm,
  btnPrimary,
  btnPrimarySm,
  fieldLabelCls,
  inputCls,
  pageContainer,
  pageHeader,
  pageSubtitle,
  pageTitle,
  sectionTitle,
  tableCell,
  tableCls,
  tableHead,
  tableHeadCell,
  tableHeadCellRight,
  tableRow,
  tableWrapper,
} from '../lib/styles';

const MONTHS_SHORT = [
  'янв','фев','мар','апр','мая','июн','июл','авг','сен','окт','ноя','дек',
];

function formatDateTime(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  const day = d.getDate();
  const month = MONTHS_SHORT[d.getMonth()];
  const yyyy = d.getFullYear();
  const today = new Date();
  const yyyyToday = today.getFullYear();
  const hh = String(d.getHours()).padStart(2, '0');
  const mm = String(d.getMinutes()).padStart(2, '0');
  if (yyyy === yyyyToday) return `${day} ${month}, ${hh}:${mm}`;
  return `${day} ${month} ${yyyy}`;
}

function formatCcyAmount(amount: string, ccy: CcyCode): string {
  const n = D(amount);
  const formatted = n.abs().toNumber().toLocaleString('ru-RU', {
    maximumFractionDigits: RATE_UI_SCALE,
    minimumFractionDigits: AMT_UI_SCALE,
  });
  const sign = n.isNegative() ? '-' : '';
  const symbol = CCY_SYMBOLS[ccy];
  return CCY_POSITION[ccy] === 'after'
    ? `${sign}${formatted} ${symbol}`
    : `${sign}${symbol}${formatted}`;
}

export function AdminRatesPage() {
  const { data: rates, isLoading, error } = useUsdtRates();
  // useCurrencies заполняет applyCurrencyMeta — сортировка/символы новых валют.
  useCurrencies();
  const [showAddForm, setShowAddForm] = useState(false);

  const sorted = useMemo(() => {
    return [...(rates ?? [])].sort(
      (a, b) => (CCY_SORT_ORDER[a.ccy] ?? 999) - (CCY_SORT_ORDER[b.ccy] ?? 999),
    );
  }, [rates]);

  return (
    <div className={pageContainer}>
      <div className={pageHeader}>
        <div>
          <h1 className={pageTitle}>Курсы валют к USDT</h1>
          <p className={pageSubtitle}>
            Курс — это сколько единиц валюты соответствуют <strong>1 USDT</strong>. Используются для
            расчёта USDT-эквивалентов на странице «Балансы», в превью прибыли при создании заявок и в дашборде.
          </p>
        </div>
        <button onClick={() => setShowAddForm((v) => !v)} className={btnPrimarySm}>
          {showAddForm ? 'Закрыть' : '+ Валюта'}
        </button>
      </div>

      {showAddForm && <AddCurrencyForm onDone={() => setShowAddForm(false)} />}

      {isLoading && <div className="text-sm text-text2">Загрузка…</div>}
      {error && (
        <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
          {(error as Error).message}
        </div>
      )}

      {sorted.length > 0 && (
        <div className={tableWrapper}>
          <table className={tableCls}>
            <thead>
              <tr className={tableHead}>
                <th className={tableHeadCell}>Валюта</th>
                <th className={tableHeadCellRight}>Курс к USDT</th>
                <th className={tableHeadCell}>Источник</th>
                <th className={tableHeadCell}>Превью</th>
                <th className={tableHeadCell}>Обновлено</th>
                <th className={`${tableHeadCell} text-right`}>Действие</th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <RateRow
                  key={r.ccy}
                  ccy={r.ccy as CcyCode}
                  rate={r.rate}
                  source={r.source}
                  autoManaged={r.autoManaged}
                  updatedAt={r.updatedAt}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="mt-4 rounded-lg border border-border bg-bg2 p-4 text-sm text-text2">
        <div className="mb-1 font-semibold text-text">Как читать курс</div>
        <p>
          Курс <code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-[12px] text-text">83.98</code> для{' '}
          <code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-[12px] text-text">RUB</code> означает, что{' '}
          <strong>1 USDT = 83.98 ₽</strong>. Чтобы перевести сумму в RUB в USDT, мы делим её на этот
          коэффициент: <code className="rounded bg-bg3 px-1.5 py-0.5 font-mono text-[12px] text-text">100 ₽ ÷ 83.98 ≈ 1.19 USDT</code>.
        </p>
        <p className="mt-2">
          Курсы стейблкоинов (<code className="font-mono text-text">USDT</code>) и доллара (
          <code className="font-mono text-text">USD</code>) приближены к 1.0. Это аппроксимация — внутрисетевые
          переводы и стоимость SWIFT не учитываются.
        </p>
      </div>
    </div>
  );
}

function RateRow({
  ccy,
  rate,
  source,
  autoManaged,
  updatedAt,
}: {
  ccy: CcyCode;
  rate: string;
  source: string | null;
  autoManaged: boolean;
  updatedAt: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(rate);
  const [error, setError] = useState<string | null>(null);
  const [sourcesOpen, setSourcesOpen] = useState(false);
  const update = useUpdateUsdtRate();
  const toggleAutoMut = useToggleAutoManaged();
  const refresh = useRefreshRate();

  useEffect(() => {
    if (!editing) {
      setDraft(rate);
      setError(null);
    }
  }, [editing, rate]);

  const numeric = D(rate);
  const reciprocal = numeric.gt(0) ? D(1).dividedBy(numeric).toDecimalPlaces(RATE_UI_SCALE).toString() : '—';

  async function save() {
    setError(null);
    const raw = draft.trim();
    if (!/^\d+(\.\d+)?$/.test(raw) || D(raw).lte(0)) {
      setError('Курс должен быть положительным числом');
      return;
    }
    // Округляем до 4 знаков (HALF_UP) — единая precision-policy системы.
    const rounded = D(raw).toDecimalPlaces(4).toString();
    try {
      await update.mutateAsync({ ccy, rate: rounded });
      setEditing(false);
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function toggleAuto() {
    setError(null);
    try {
      await toggleAutoMut.mutateAsync({ ccy, autoManaged: !autoManaged });
    } catch (err) {
      setError((err as Error).message);
    }
  }

  async function doRefresh() {
    setError(null);
    try {
      const result = await refresh.mutateAsync(ccy);
      if (result.status === 'failed') {
        setError(`Не удалось обновить: ${result.error ?? 'unknown error'}`);
      }
    } catch (err) {
      setError((err as Error).message);
    }
  }

  function onKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault();
      void save();
    } else if (e.key === 'Escape') {
      setEditing(false);
    }
  }

  return (
    <>
      <tr className={tableRow}>
        <td className={`${tableCell}`}>
          <div className="flex items-center gap-2">
            <span className="inline-flex h-7 w-7 items-center justify-center rounded-md border border-border bg-bg font-mono text-base font-semibold text-text">
              {CCY_SYMBOLS[ccy]}
            </span>
            <span className="font-mono text-sm font-semibold text-text">{ccy}</span>
          </div>
        </td>
        <td className={`${tableCell} text-right`}>
          {editing ? (
            <input
              autoFocus
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={onKeyDown}
              className="ml-auto block h-8 w-32 rounded-md border border-border bg-bg px-2 text-right font-mono text-sm tabular-nums text-text outline-none transition-colors duration-150 focus:border-accent focus:shadow-focus"
              inputMode="decimal"
            />
          ) : (
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="font-mono text-sm font-semibold tabular-nums text-text underline-offset-4 transition-colors duration-150 hover:text-accent hover:underline"
              title="Редактировать"
            >
              {numeric.toString()}
            </button>
          )}
        </td>
        <td className={tableCell}>
          <SourceBadge source={source} autoManaged={autoManaged} onToggleAuto={toggleAuto} />
        </td>
        <td className={`${tableCell} text-xs text-text2`}>
          <div>
            1 USDT ={' '}
            <span className="font-mono font-medium text-text">{formatCcyAmount(rate, ccy)}</span>
          </div>
          {numeric.gt(0) && (
            <div>
              1 {ccy} ={' '}
              <span className="font-mono font-medium text-text">{reciprocal} USDT</span>
            </div>
          )}
        </td>
        <td className={`${tableCell} font-mono text-xs text-text2`}>{formatDateTime(updatedAt)}</td>
        <td className={`${tableCell} text-right`}>
          {editing ? (
            <div className="inline-flex gap-1.5">
              <button
                type="button"
                disabled={update.isPending || draft === rate}
                onClick={() => void save()}
                className={btnPrimarySm}
              >
                {update.isPending ? 'Сохранение…' : 'Сохранить'}
              </button>
              <button type="button" onClick={() => setEditing(false)} className={btnGhostSm}>
                Отмена
              </button>
            </div>
          ) : (
            <div className="inline-flex gap-1.5">
              <button
                type="button"
                disabled={refresh.isPending}
                onClick={() => void doRefresh()}
                className={btnGhostSm}
                title="Обновить через сконфигурированные источники"
              >
                <IconRefresh className={refresh.isPending ? 'animate-spin' : undefined} />
                Обновить
              </button>
              <button
                type="button"
                onClick={() => setSourcesOpen(true)}
                className={btnGhostSm}
                title="Настроить цепочку провайдеров"
              >
                <IconSettings />
                Источники
              </button>
              <button
                type="button"
                onClick={() => setEditing(true)}
                className={btnGhostSm}
                title="Редактировать вручную"
              >
                ✎ Изменить
              </button>
            </div>
          )}
        </td>
      </tr>
      {error && (
        <tr>
          <td colSpan={6} className="px-3 pb-2">
            <div className="rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-xs text-danger">
              {error}
            </div>
          </td>
        </tr>
      )}
      {sourcesOpen && <RateSourcesModal ccy={ccy} onClose={() => setSourcesOpen(false)} />}
    </>
  );
}

function SourceBadge({
  source,
  autoManaged,
  onToggleAuto,
}: {
  source: string | null;
  autoManaged: boolean;
  onToggleAuto: () => void;
}) {
  const label = source ?? '—';
  const cls =
    source === 'manual' || source === null
      ? 'border-border bg-bg3 text-text2'
      : 'border-[var(--accent-border)] bg-[var(--accent-bg)] text-accent';
  return (
    <div className="inline-flex items-center gap-2">
      <span className={`inline-flex items-center rounded-md border px-2 py-0.5 text-[11px] font-mono ${cls}`}>
        {label}
      </span>
      <label className="inline-flex items-center gap-1 text-[10px] text-text2" title="Автообновление по cron">
        <input type="checkbox" checked={autoManaged} onChange={onToggleAuto} />
        авто
      </label>
    </div>
  );
}

/**
 * Форма добавления валюты. Создаёт запись в reference.currencies + стартовый
 * курс к USDT (source='manual'). Новая валюта сразу появляется во всех
 * ccy-списках (форма заявки, балансы чатов, телеграм-правила).
 */
function AddCurrencyForm({ onDone }: { onDone: () => void }) {
  const create = useCreateCurrency();
  const [code, setCode] = useState('');
  const [symbol, setSymbol] = useState('');
  const [position, setPosition] = useState<'before' | 'after'>('before');
  const [scale, setScale] = useState(2);
  const [rate, setRate] = useState('');

  function submit(e: React.FormEvent) {
    e.preventDefault();
    // Квантуем курс до точности хранения до отправки — MoneyStr принимает ≤4 знака.
    const rateRaw = rate.trim().replace(',', '.');
    if (!/^\d+(\.\d+)?$/.test(rateRaw) || D(rateRaw).lte(0)) return;
    void create
      .mutateAsync({
        code: code.trim().toUpperCase(),
        symbol: symbol.trim() || code.trim().toUpperCase(),
        position,
        scale,
        initialUsdtRate: D(rateRaw).toDecimalPlaces(MONEY_SCALE).toString(),
      })
      .then(() => onDone());
  }

  return (
    <form onSubmit={submit} className="mb-6 rounded-lg border border-border bg-bg2 p-5">
      <h2 className={`${sectionTitle} mb-4`}>Новая валюта</h2>
      <div className="grid grid-cols-2 gap-4 sm:grid-cols-5">
        <label className="block">
          <span className={fieldLabelCls}>Код (ISO)</span>
          <input
            type="text"
            required
            pattern="[A-Za-z0-9]{2,12}"
            placeholder="TRY"
            value={code}
            onChange={(e) => setCode(e.target.value.toUpperCase())}
            className={`${inputCls} font-mono uppercase`}
          />
        </label>
        <label className="block">
          <span className={fieldLabelCls}>Символ</span>
          <input
            type="text"
            maxLength={8}
            placeholder="₺ (пусто = код)"
            value={symbol}
            onChange={(e) => setSymbol(e.target.value)}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className={fieldLabelCls}>Позиция символа</span>
          <div className="flex gap-1">
            {(['before', 'after'] as const).map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPosition(p)}
                className={`flex-1 rounded-md border px-2 py-1.5 text-xs transition-colors duration-150 ${
                  position === p
                    ? 'border-[var(--accent-border)] bg-[var(--accent-bg)] text-accent'
                    : 'border-border bg-bg text-text2 hover:text-text'
                }`}
              >
                {p === 'before' ? '$100' : '100 ₽'}
              </button>
            ))}
          </div>
        </label>
        <label className="block">
          <span className={fieldLabelCls}>Знаков после запятой</span>
          <input
            type="number"
            min={0}
            max={8}
            value={scale}
            onChange={(e) => setScale(Number(e.target.value))}
            className={inputCls}
          />
        </label>
        <label className="block">
          <span className={fieldLabelCls}>Курс к USDT</span>
          <input
            type="text"
            required
            inputMode="decimal"
            placeholder="34.5"
            value={rate}
            onChange={(e) => setRate(e.target.value.replace(',', '.'))}
            className={`${inputCls} font-mono`}
          />
          <span className="mt-1 block text-[10px] text-text-subtle">
            сколько {code || 'единиц'} за 1 USDT
          </span>
        </label>
      </div>
      {create.error && (
        <div className="mt-4 rounded-md border border-[var(--red-border)] bg-[var(--red-bg)] px-3 py-2 text-sm text-danger">
          {(create.error as Error).message}
        </div>
      )}
      <div className="mt-5 flex justify-end border-t border-border-subtle pt-4">
        <button type="submit" disabled={create.isPending} className={btnPrimary}>
          {create.isPending ? 'Создание…' : 'Добавить валюту'}
        </button>
      </div>
    </form>
  );
}
